import { useQueryClient } from '@tanstack/react-query';
import { effectivePermissionSetHas } from '@utazzo/shared';
import {
  ArrowLeft,
  ArrowLeftRight,
  Check,
  CheckCheck,
  ExternalLink,
  FileText,
  History,
  MapPin,
  Mic,
  Paperclip,
  Phone,
  Plus,
  Reply,
  Search,
  Send,
  Smile,
  Trash2,
  X,
} from 'lucide-react';
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { installCallAudioUnlock } from './call-tones';
import { LinkifiedText } from './LinkifiedText';
import LocationPicker, { type PickedLocation } from './LocationPicker';
import MediaBubble from './MediaBubble';
import { SoftphoneContext, useSoftphone } from './softphone-context';
import { useAutoAnswer } from './use-auto-answer';
import {
  useWhatsAppInbox,
  useWhatsAppThread,
  useSendWhatsApp,
  useWhatsAppTemplates,
} from './use-whatsapp';
import { useWhatsAppCall } from './use-whatsapp-call';
import { playIncomingChime, useWhatsAppStream } from './use-whatsapp-stream';
import { voiceClipToMp3 } from './voice-note';
import { fetchCallStatus } from './whatsapp-calls.api';
import { listWhatsAppConversations, uploadWhatsAppAttachment } from './whatsapp.api';
import { WhatsAppCallHistory } from './WhatsAppCallHistory';
import { WhatsAppCallScreen } from './WhatsAppCallScreen';
import {
  onOpenWhatsAppConversation,
  onOpenWhatsAppForLead,
  onPickUpWhatsAppCall,
} from './widget-bus';

// The emoji picker is ~1MB of emoji data - load it only when the smiley is
// first opened, never on app boot.
const EmojiPicker = lazy(() => import('emoji-picker-react'));

import type {
  SendWhatsAppPayload,
  WhatsAppCallPermission,
  WhatsAppTemplateDef,
  WhatsAppTemplateView,
  WhatsAppThreadMessage,
} from './whatsapp.api';
import type { LeadPhoneTarget, LeadWhatsAppMessage } from '@/features/leads/leads.api';

import { useCurrentUser } from '@/features/auth/use-auth';
import { useItinerariesList } from '@/features/itineraries/use-itineraries';
import { useLeadsList, useLeadWhatsApp, useSendLeadWhatsApp } from '@/features/leads/use-leads';
import { useMyEffectivePermissions, useMyProfile } from '@/features/my-profile/use-my-profile';
import { isPushCovering } from '@/features/push/web-push-client';
import { useLeadDocumentRequests } from '@/features/traveller-documents/use-traveller-documents';
import { extractServerError } from '@/lib/api-error';
import { isStandaloneApp } from '@/lib/device';

/**
 * "Not on WhatsApp" pill - the provider bounced the last send to this number
 * as undeliverable (Meta 131026). Same markup in the widget header and the
 * inbox rows.
 */
export const NotOnWhatsAppBadge = ({
  compact = false,
}: {
  compact?: boolean;
}): React.JSX.Element => (
  <span
    title="WhatsApp could not deliver to this number: it is not registered on WhatsApp. Call or email instead. Clears automatically if the number replies or a later message is delivered."
    className={`inline-flex shrink-0 items-center rounded-full border border-red-200 bg-red-50 font-medium text-red-700 ${
      compact ? 'px-1.5 py-px text-[10px]' : 'px-2 py-0.5 text-[11px]'
    }`}
  >
    Not on WhatsApp
  </span>
);

/** Template slot that takes the documents upload link (documents_upload / documents_reminder). */
const isUploadLinkParam = (label: string): boolean => /upload link/i.test(label);

// ---------------------------------------------------------------------------
// Floating WhatsApp widget (docs/integrations/doubletick-whatsapp.md).
//
// A WhatsApp-green bubble sits bottom-right on every admin page with the
// total-unread badge. The panel has three views:
//   - list: every thread the actor may see (API scopes agents to assigned
//     leads) - tap a row for the chat
//   - new:  pick one of your leads to START a chat (first contact is always
//     a template - Meta's rule - the composer handles that)
//   - chat: the conversation, WhatsApp-look, driven either by conversation id
//     (from the inbox) or by lead id (new chat / lead-page button)
//
// Meta's 24h rule drives the composer exactly like the API: free text while
// the window is open, template picker once it closes (or before first reply).
// ---------------------------------------------------------------------------

// Template catalog lives server-side (template-catalog.ts); the picker only
// receives entries approved on the DoubleTick account - see /whatsapp/templates.

type WidgetView =
  | { kind: 'list' }
  | { kind: 'new' }
  | { kind: 'convo'; id: string }
  | {
      kind: 'lead';
      id: string;
      name?: string;
      destination?: string;
      /** Which of the customer's two numbers this thread is on. */
      phoneTarget?: LeadPhoneTarget;
    };

export const StatusTick = ({ status }: { status: string }): React.JSX.Element | null => {
  if (status === 'READ') return <CheckCheck className="h-3.5 w-3.5 text-sky-500" />;
  if (status === 'DELIVERED') return <CheckCheck className="h-3.5 w-3.5 text-zinc-400" />;
  // A failed send shows no tick rather than a red one: the agent is not the
  // person who can fix it, and claiming "sent" would be a lie. The admin
  // WhatsApp log has the failure with the provider's reason.
  if (status === 'FAILED') return null;
  return <Check className="h-3.5 w-3.5 text-zinc-400" />;
};

const hhmm = (d: Date): string =>
  `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

/** Message-bubble stamp: today -> "14:05", older -> "08 Aug, 14:05". */
const timeFull = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (d.toDateString() === new Date().toDateString()) return hhmm(d);
  return `${d.getDate().toString().padStart(2, '0')} ${d.toLocaleString('en', { month: 'short' })}, ${hhmm(d)}`;
};

const initialsOf = (name: string | null, phone: string): string => {
  const n = (name ?? '').trim();
  if (!n) return phone.slice(-2);
  return n
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

/**
 * Owns the one softphone + live stream and provides SoftphoneContext to
 * everything under it, including the /whatsapp inbox page - which is why the
 * app shell renders its main column as `children` of this component. The
 * bubble itself just opens that page.
 */
export const WhatsAppWidget = ({ children }: { children?: React.ReactNode }): React.JSX.Element => {
  // Permission-gated like every other module (ops.whatsapp.read in the
  // registry): admin / sales_lead / sales_agent hold it by default, and an
  // ADMIN can grant it to any custom role from /roles. The API enforces the
  // same codes, so hiding here is UX, not security.
  const effectivePermissions = useMyEffectivePermissions();
  const canChat = effectivePermissionSetHas(effectivePermissions, 'ops.whatsapp.read');
  // Answering needs the write permission (the accept endpoint enforces it),
  // so read-only users are never rung.
  const canCall = effectivePermissionSetHas(effectivePermissions, 'ops.whatsapp.write');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // "Open" = the inbox page is showing; which chat is open is in its URL.
  const open = pathname === '/whatsapp' || pathname.startsWith('/whatsapp/');
  const view = useMemo<WidgetView>(() => {
    if (!open) return { kind: 'list' };
    const params = new URLSearchParams(search);
    const c = params.get('c');
    const lead = params.get('lead');
    if (c) return { kind: 'convo', id: c };
    if (lead) return { kind: 'lead', id: lead };
    return { kind: 'list' };
  }, [open, search]);
  /** Open the inbox page on a chat. Lead name/destination ride on router state. */
  const goTo = useCallback(
    (v: WidgetView) => {
      if (v.kind === 'convo') navigate(`/whatsapp?c=${encodeURIComponent(v.id)}`);
      else if (v.kind === 'lead') {
        navigate(`/whatsapp?lead=${encodeURIComponent(v.id)}`, {
          state: { name: v.name ?? null, destination: v.destination ?? null },
        });
      } else navigate('/whatsapp');
    },
    [navigate],
  );

  const inbox = useWhatsAppInbox(canChat);

  // The softphone lives here, not in the chat view, so a call survives
  // closing the chat or changing page and can be minimised.
  const softphone = useWhatsAppCall();

  // Any tap / key unlocks audio, so a ring or message chime that comes later
  // is heard even if nobody touched the app since it loaded (browsers keep
  // an AudioContext suspended until the first gesture, on laptops too).
  useEffect(() => installCallAudioUnlock(), []);

  // Ask once for desktop-notification permission the first time the widget
  // is opened - a user gesture, so browsers show the prompt. Not in the
  // installed app: there the "Turn on notifications" banner asks, with an
  // explanation - a bare prompt invites a "Block" that push can't undo.
  useEffect(() => {
    if (
      open &&
      !isStandaloneApp() &&
      'Notification' in window &&
      Notification.permission === 'default'
    ) {
      void Notification.requestPermission();
    }
  }, [open]);

  // Refs so the stream callback sees current state without resubscribing.
  const openRef = useRef(open);
  openRef.current = open;
  const viewRef = useRef(view);
  viewRef.current = view;

  // Live updates: SSE hints beat the polling intervals, so a customer's
  // message appears the moment the webhook lands.
  useWhatsAppStream({
    enabled: canChat,
    onLinesEvent: softphone.onLinesEvent,
    onCallEvent: (e) => {
      // Call history panels refresh on every transition (recording, duration).
      void queryClient.invalidateQueries({ queryKey: ['whatsapp-calls'] });
      // The API sends these to the lead's assigned agent only, so anything
      // that arrives here is ours to ring for.
      if (canCall) softphone.onCallEvent(e);
    },
    onEvent: (e) => {
      void queryClient.invalidateQueries({ queryKey: ['whatsapp', 'inbox'] });
      void queryClient.invalidateQueries({ queryKey: ['whatsapp', 'thread', e.conversationId] });
      // Lead-tab threads key on lead id, which the hint doesn't carry -
      // refresh the currently open one.
      const v = viewRef.current;
      if (v.kind === 'lead') {
        void queryClient.invalidateQueries({ queryKey: ['leads', v.id, 'whatsapp'] });
      }

      if (e.direction !== 'IN' || e.event !== 'message') return;
      const chatOnScreen =
        !document.hidden &&
        openRef.current &&
        (v.kind === 'convo' ? v.id === e.conversationId : v.kind === 'lead');
      // The stream is a broadcast; only ping when this chat is actually in
      // MY scoped inbox (the API fences agents to their assigned leads).
      void queryClient
        .fetchQuery({ queryKey: ['whatsapp', 'inbox'], queryFn: listWhatsAppConversations })
        .then((fresh) => {
          const mine = fresh.conversations.find((c) => c.id === e.conversationId);
          if (!mine) return;
          // The chime plays for every incoming message of mine, open chat
          // included - like WhatsApp itself. Only the desktop notification
          // is skipped when the chat is already on screen.
          playIncomingChime();
          if (chatOnScreen) return;
          // A device with web push on gets this message as a push already.
          if (
            !isPushCovering('whatsappMessages') &&
            'Notification' in window &&
            Notification.permission === 'granted'
          ) {
            const who = mine.lead?.customerName ?? mine.contactName ?? `+${mine.phone}`;
            const n = new Notification(`WhatsApp · ${who}`, {
              body: mine.lastMessage?.body ?? 'New message - open the chat widget to reply.',
              tag: `whatsapp-${e.conversationId}`,
            });
            n.onclick = () => {
              window.focus();
              goTo({ kind: 'convo', id: e.conversationId });
              n.close();
            };
          }
        })
        .catch(() => undefined);
    },
  });

  // Lead pages (and anything else) can pop the widget straight onto a lead's
  // chat via the widget bus.
  useEffect(() => {
    return onOpenWhatsAppForLead((req) => {
      goTo({ kind: 'lead', id: req.leadId, name: req.name, destination: req.destination });
    });
  }, [goTo]);

  // A tapped phone notification for a chat. Subscribed only once the user is
  // known to have chat access - until then the bus keeps the request parked.
  useEffect(() => {
    if (!canChat) return;
    return onOpenWhatsAppConversation((conversationId) => {
      goTo({ kind: 'convo', id: conversationId });
    });
  }, [canChat, goTo]);

  // A tapped "Incoming WhatsApp call" notification. The SSE ring went to a
  // page that wasn't open, so look the call up and ring here if it still is.
  const onSoftphoneCallEvent = softphone.onCallEvent;
  // The notification's "Answer" button: answer the call as soon as it rings
  // here. Kept as state (not a direct call) because the ring may still be
  // on its way - from this fetch, or already up from the live stream.
  const [autoAnswerCallId, setAutoAnswerCallId] = useState<string | null>(null);
  useEffect(() => {
    if (!canCall) return;
    return onPickUpWhatsAppCall((callId, opts) => {
      fetchCallStatus(callId)
        .then((call) => {
          if (call && call.direction === 'INCOMING' && call.status === 'INITIATED') {
            onSoftphoneCallEvent({
              callId: call.callId,
              direction: call.direction,
              status: call.status,
              phone: call.phone,
              contactName: call.contactName,
              conversationId: call.conversationId,
              leadId: call.leadId,
              startedAt: call.startedAt,
              canAnswer: Boolean(call.offerSdp),
            });
            if (opts.answer) setAutoAnswerCallId(call.callId);
            return;
          }
          toast.info('That call has already ended.');
          if (call?.conversationId) goTo({ kind: 'convo', id: call.conversationId });
        })
        .catch(() => toast.error('Could not load that call.'));
    });
  }, [canCall, goTo, onSoftphoneCallEvent]);

  const clearAutoAnswer = useCallback(() => setAutoAnswerCallId(null), []);
  useAutoAnswer(
    autoAnswerCallId,
    softphone.state.status,
    softphone.state.callId,
    softphone.answer,
    clearAutoAnswer,
  );

  if (!canChat) return <>{children}</>;
  const totalUnread = inbox.data?.totalUnread ?? 0;

  return (
    <SoftphoneContext.Provider value={softphone}>
      {children}
      {/* Call window / minimised pill - above everything, on every page. */}
      <WhatsAppCallScreen softphone={softphone} />
      {/* The bubble opens the inbox page; hidden while already on it. */}
      {!open && (
        <button
          type="button"
          aria-label="WhatsApp chats"
          onClick={() => goTo({ kind: 'list' })}
          className="fixed right-5 bottom-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
        >
          <FaWhatsapp className="h-8 w-8" />
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-bold text-white shadow">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>
      )}
    </SoftphoneContext.Provider>
  );
};

// ---------- New chat: pick one of your leads ----------

export const NewChatView = ({
  onBack,
  onPick,
}: {
  onBack: () => void;
  onPick: (
    leadId: string,
    name: string,
    destination?: string,
    phoneTarget?: LeadPhoneTarget,
  ) => void;
}): React.JSX.Element => {
  const me = useCurrentUser();
  const [search, setSearch] = useState('');
  // Debounced copy of `search` - the query fires 300ms after the operator
  // stops typing instead of once per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Infinite scroll: the list starts small and grows the SAME query's
  // pageSize by +10 whenever the operator nears the bottom (the inbox
  // aside's "Load more" pattern - keepPreviousData stops the flash).
  const [pageSize, setPageSize] = useState(15);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  // Agents only see their own leads; ADMIN / SALES_LEAD search everything.
  // The server additionally applies its normal visibility scope.
  const leads = useLeadsList(
    {
      page: 1,
      pageSize,
      sortBy: 'createdAt',
      sortDir: 'desc',
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      ...(me?.role === 'SALES_AGENT' ? { assignedAgentId: me.id } : {}),
    },
    { keepPreviousData: true },
  );
  // Either number makes a lead chattable - an alternate-only customer used
  // to be invisible here.
  const rows = (leads.data?.data ?? []).filter(
    (l) => l.customer.mobileNumber ?? l.customer.altPhone,
  );
  const totalLeads = leads.data?.pagination.totalItems ?? 0;
  const hasMore = (leads.data?.data.length ?? 0) < totalLeads;

  const onListScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 80) return;
    if (leads.isFetching || !hasMore) return;
    setPageSize((s) => s + 10);
  };

  const onSearchChange = (value: string): void => {
    setSearch(value);
    setPageSize(15);
  };

  return (
    <>
      <div className="flex items-center gap-2 bg-[#075E54] px-3 py-3 text-white">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="rounded p-1 hover:bg-white/10"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="text-[15px] font-semibold">New chat</span>
      </div>
      <div className="border-b border-zinc-100 p-2">
        <div className="flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3">
          <Search className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          <input
            autoFocus
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search your leads (name, ID, phone)…"
            className="h-9 w-full bg-transparent text-sm outline-none"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto" onScroll={onListScroll}>
        {leads.isFetching && (
          <p className="border-b border-zinc-100 px-4 py-1.5 text-[11px] text-zinc-400">
            Searching…
          </p>
        )}
        {leads.isLoading ? (
          <p className="p-4 text-sm text-zinc-500">Loading leads…</p>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-zinc-500">
            No leads with a phone number found{search ? ' for this search' : ''}.
          </p>
        ) : (
          rows.map((l) => {
            const destination = l.destinationCity?.name ?? l.destination?.name ?? undefined;
            const pick = (phoneTarget: LeadPhoneTarget): void =>
              onPick(l.id, l.customer.displayName, destination, phoneTarget);
            const primary = l.customer.mobileNumber;
            const alternate = l.customer.altPhone;
            return (
              <div
                key={l.id}
                className="flex items-center gap-3 border-b border-zinc-100 px-3 py-2.5"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-semibold text-emerald-800">
                  {initialsOf(l.customer.displayName, primary ?? alternate ?? '')}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-medium text-zinc-900">
                    {l.customer.displayName}
                  </span>
                  <span className="block truncate text-[12px] text-zinc-500">{l.reference}</span>
                  {/* One button per number on file: each opens that number's
                      own thread (conversations are keyed on the number). */}
                  <span className="mt-1 flex flex-wrap gap-1.5">
                    {primary && (
                      <button
                        type="button"
                        onClick={() => pick('PRIMARY')}
                        className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-700 transition-colors hover:bg-emerald-100 hover:text-emerald-800"
                      >
                        {alternate ? `Primary · ${primary}` : primary}
                      </button>
                    )}
                    {alternate && (
                      <button
                        type="button"
                        onClick={() => pick('ALTERNATE')}
                        className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-700 transition-colors hover:bg-emerald-100 hover:text-emerald-800"
                      >
                        Alternate · {alternate}
                      </button>
                    )}
                  </span>
                </span>
              </div>
            );
          })
        )}
        {rows.length > 0 && hasMore && (
          <p className="p-3 text-center text-[11px] text-zinc-400">
            {leads.isFetching ? 'Loading more…' : `Scroll for more (${totalLeads} leads)`}
          </p>
        )}
      </div>
    </>
  );
};

// ---------- Chat view (by conversation id OR by lead id) ----------

export type ChatTarget =
  | { kind: 'convo'; id: string }
  | {
      kind: 'lead';
      id: string;
      name?: string;
      destination?: string;
      phoneTarget?: LeadPhoneTarget;
    };

/** Common message shape both endpoints already satisfy. */
type AnyMessage = WhatsAppThreadMessage | LeadWhatsAppMessage;

// An itinerary share link on our own website (not the /share/p/ preview).
const SHARE_LINK_RE =
  /https?:\/\/(?:(?:[\w-]+\.)*utazzo[\w-]*(?:\.[a-z]+)+|localhost:\d+)\/share\/(?!p\/)[\w-]+/i;

/**
 * The link-preview card WhatsApp shows the customer for an itinerary share
 * link: the page's og:image, served by the website at `<link>/og-image`.
 * Drawn only on plain text messages - WhatsApp never previews a link inside
 * a template. Disappears if the image cannot load.
 */
const ShareLinkPreview = ({ text }: { text: string }): React.JSX.Element | null => {
  const [broken, setBroken] = useState(false);
  const link = SHARE_LINK_RE.exec(text)?.[0];
  if (!link || broken) return null;
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="mb-1 block overflow-hidden rounded-md bg-black/5 no-underline"
    >
      <img
        src={`${link}/og-image`}
        alt="Itinerary preview"
        loading="lazy"
        onError={() => setBroken(true)}
        className="aspect-[1200/630] w-full object-cover"
      />
      <span className="block truncate px-2 py-1 text-[11px] text-zinc-500">
        {new URL(link).host}
      </span>
    </a>
  );
};

/** What hovering the call button says, from the customer's answer on record. */
const callPermissionHint = (
  permission: WhatsAppCallPermission | null,
  contactName: string | null | undefined,
): string => {
  const who = contactName?.trim() || 'this customer';
  if (!permission) {
    return `No call permission on record for ${who} - send the call permission request first`;
  }
  if (permission.response === 'reject') {
    return `Call permission denied - you can't call ${who} until they allow it`;
  }
  if (permission.isPermanent) return 'Call on WhatsApp - permission allowed permanently';
  if (permission.expiresAt && new Date(permission.expiresAt).getTime() < Date.now()) {
    return `Call permission expired - send the call permission request to ${who} again`;
  }
  return 'Call on WhatsApp - permission allowed temporarily';
};

/** A template's header media is an image unless the template says otherwise. */
const templateMediaKind = (view: WhatsAppTemplateView | null | undefined): string =>
  view?.headerFormat === 'VIDEO' || view?.headerFormat === 'DOCUMENT' ? view.headerFormat : 'IMAGE';

/**
 * A template's buttons, stacked under the bubble text the way WhatsApp draws
 * them. URL and call buttons open like the customer's would; a quick reply
 * is the customer's to tap, so here it is only shown.
 */
const TemplateButtons = ({
  buttons,
}: {
  buttons: WhatsAppTemplateView['buttons'];
}): React.JSX.Element => (
  <div className="-mx-2.5 mt-1.5 -mb-1.5 border-t border-black/10">
    {buttons.map((btn, i) => {
      const href =
        btn.type === 'URL'
          ? btn.url
          : btn.type === 'PHONE_NUMBER' && btn.phoneNumber
            ? `tel:${btn.phoneNumber}`
            : null;
      const Icon = btn.type === 'URL' ? ExternalLink : btn.type === 'PHONE_NUMBER' ? Phone : Reply;
      const className = `flex items-center justify-center gap-1.5 px-2.5 py-2 text-[13.5px] font-medium text-sky-700 no-underline ${
        i > 0 ? 'border-t border-black/10' : ''
      }`;
      const content = (
        <>
          <Icon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{btn.text}</span>
        </>
      );
      return href ? (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`${className} hover:bg-black/5`}
        >
          {content}
        </a>
      ) : (
        <div key={i} className={className}>
          {content}
        </div>
      );
    })}
  </div>
);

export const ChatView = ({
  target,
  onBack,
  onSwitchNumber,
  variant = 'widget',
}: {
  target: ChatTarget;
  /** Absent on the full-page inbox, where the list is always beside the chat. */
  onBack?: () => void;
  /** Lead threads only: move to the customer's other number. */
  onSwitchNumber: (phoneTarget: LeadPhoneTarget) => void;
  /** `page`: light header matching the inbox page; `widget`: WhatsApp green. */
  variant?: 'widget' | 'page';
}): React.JSX.Element => {
  const isPage = variant === 'page';
  // Header palette per variant - the controls are identical.
  const hdr = isPage
    ? {
        bar: 'flex items-center gap-2 border-b border-zinc-200 bg-white px-4 py-2.5 text-zinc-900',
        avatar:
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-semibold text-emerald-800',
        sub: 'block truncate text-[11.5px] text-zinc-500',
        btn: 'rounded-md p-1.5 text-zinc-600 hover:bg-zinc-100 disabled:opacity-40',
        btnOn: 'bg-zinc-200',
      }
    : {
        bar: 'flex items-center gap-2 bg-[#075E54] px-3 py-2.5 text-white',
        avatar:
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-semibold',
        sub: 'block truncate text-[11px] text-emerald-100',
        btn: 'rounded p-1.5 hover:bg-white/10 disabled:opacity-40',
        btnOn: 'bg-white/20',
      };
  const me = useCurrentUser();
  const myProfile = useMyProfile();
  const navigate = useNavigate();

  // Hooks must run unconditionally - each is enabled only for its mode.
  const convoThread = useWhatsAppThread(target.kind === 'convo' ? target.id : null);
  const templatesQuery = useWhatsAppTemplates();
  const templates: WhatsAppTemplateDef[] = templatesQuery.data?.templates ?? [];
  // Which number this lead thread is on. Conversations are keyed on the
  // number, so the two numbers are two separate threads.
  const phoneTarget: LeadPhoneTarget =
    target.kind === 'lead' ? (target.phoneTarget ?? 'PRIMARY') : 'PRIMARY';
  const leadThread = useLeadWhatsApp(
    target.kind === 'lead' ? target.id : '',
    target.kind === 'lead',
    phoneTarget,
  );
  const sendConvo = useSendWhatsApp();
  const sendLead = useSendLeadWhatsApp();

  const [draft, setDraft] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateParams, setTemplateParams] = useState<string[]>([]);
  const [buttonParams, setButtonParams] = useState<string[]>([]);
  // Window open me bhi templates bhejne hote hain (itinerary/payment) - this
  // toggles the template panel alongside the free-text composer.
  const [showTemplatePanel, setShowTemplatePanel] = useState(false);
  // Attachment upload in flight (presign -> S3 PUT -> send).
  const [uploading, setUploading] = useState(false);
  // Kept as a sink: the setters stay where the failures happen, but nothing
  // is rendered from it (see the admin WhatsApp log).
  const [, setComposerError] = useState<string | null>(null);
  // WhatsApp-style "+" attach menu and emoji picker toggles.
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Voice note recording (MediaRecorder). Chrome/Safari record AAC-in-MP4,
  // Firefox records Opus-in-OGG - both WhatsApp-deliverable. webm (very old
  // Chrome) is refused by the presign endpoint with a clear message.
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordIntentRef = useRef<'send' | 'cancel'>('cancel');
  // Location panel: map picker (LocationPicker) below the composer.
  const [showLocationPanel, setShowLocationPanel] = useState(false);
  // Call log panel: WhatsApp voice calls on this thread, with recordings.
  const [showCallPanel, setShowCallPanel] = useState(false);
  // The widget-wide softphone (use-whatsapp-call.ts) - one call at a time.
  const softphone = useSoftphone();
  const inCall = !['idle', 'failed', 'ended'].includes(softphone.state.status);
  // One line per DoubleTick calling license; dialling waits when all are on
  // calls. The SSE stream re-enables the button the moment one frees.
  const lines = softphone.state.lines;
  const linesFull = lines ? lines.busy >= lines.total : false;
  // True once the seller manually picks a version - stops the "follow the
  // latest" default from overriding their choice when fresh data lands.
  const [versionTouched, setVersionTouched] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const isLead = target.kind === 'lead';
  const loading = isLead ? leadThread.isLoading : convoThread.isLoading;
  const messages: AnyMessage[] =
    (isLead ? leadThread.data?.messages : convoThread.data?.messages) ?? [];
  const phone = isLead ? leadThread.data?.phone : convoThread.data?.conversation.phone;
  // The customer's latest answer to a call-permission request, if any.
  const callPermission =
    (isLead ? leadThread.data?.callPermission : convoThread.data?.callPermission) ?? null;
  const callAllowedForGood = callPermission?.response === 'accept' && callPermission.isPermanent;
  // Both numbers on file (lead threads only) - the header offers the other one.
  const leadPhones = isLead ? leadThread.data?.phones : undefined;
  const otherPhone =
    (phoneTarget === 'PRIMARY' ? leadPhones?.alternate : leadPhones?.primary) ?? null;
  // Typed text always sends (the API wraps it in a template past the 24h
  // window), but media and location have no way out of a closed window - so
  // those controls are simply off rather than failing on send.
  const canSendMedia =
    (isLead ? leadThread.data?.windowOpen : convoThread.data?.windowOpen) ?? false;
  // Meta bounced the last send as undeliverable (131026): no WhatsApp account
  // on this number. Shown, not enforced - the number may register later and
  // a reply or a delivered send clears it server-side.
  const notOnWhatsApp =
    (isLead ? leadThread.data?.notOnWhatsApp : convoThread.data?.notOnWhatsApp) ?? false;
  const mediaBlockedHint = 'Available once the customer replies (24h window)';
  // A composer error belongs to the attempt that caused it: drop it when the
  // chat changes, or when the window reopens and the message would now work.
  useEffect(() => {
    setComposerError(null);
  }, [target.kind, target.id, canSendMedia]);

  const contactName = isLead
    ? (target.name ?? leadThread.data?.conversation?.contactName ?? null)
    : (convoThread.data?.lead?.customerName ?? convoThread.data?.conversation.contactName ?? null);
  const destination = isLead
    ? (target.destination ?? null)
    : (convoThread.data?.lead?.destination ?? null);
  const leadForNav = isLead
    ? { id: target.id, reference: 'lead' }
    : (convoThread.data?.lead ?? null);
  // Lead id + owner in the header - admin and sales lead only. The API sends
  // `assignedAgentName` to those roles alone, so its presence is the gate.
  const ownerLead =
    !isLead && convoThread.data?.lead && convoThread.data.lead.assignedAgentName !== undefined
      ? convoThread.data.lead
      : null;
  const sending = sendConvo.isPending || sendLead.isPending;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const template = templates.find((t) => t.name === templateName) ?? null;

  // Published itinerary versions of this lead - options for a template's
  // URL-button suffix ("View Itinerary"). Fetched only while a template
  // with a button param is picked and we know the lead.
  const chatLeadId = isLead ? target.id : (convoThread.data?.lead?.id ?? null);
  const anyTemplateNeedsLink = templates.some((x) => (x.buttonParams?.length ?? 0) > 0);
  const needsVersionPicker = Boolean(template?.buttonParams?.length && chatLeadId);
  const itinerariesQuery = useItinerariesList(
    {
      page: 1,
      pageSize: 50,
      sortBy: 'createdAt',
      sortDir: 'desc',
      leadId: chatLeadId ?? undefined,
      onlyLatestRevisions: false,
    },
    { enabled: Boolean(chatLeadId && anyTemplateNeedsLink) },
  );
  // Sendable versions: published (share link exists) and not deactivated.
  const versionOptions = useMemo(() => {
    const rows = itinerariesQuery.data?.data ?? [];
    return rows
      .filter((it) => it.shareUrl && it.status !== 'CANCELLED')
      .sort((a, b) => {
        if (a.isLatestRevision !== b.isLatestRevision) return a.isLatestRevision ? -1 : 1;
        return b.revisionNumber - a.revisionNumber;
      })
      .map((it) => ({
        // The /share/ resolver accepts either the quote id or the token -
        // whatever slug the shareUrl itself carries is always right.
        value: it.shareUrl!.split('/').filter(Boolean).pop() ?? '',
        label: `v${it.revisionNumber}${it.isLatestRevision ? ' (latest)' : ''} · ${it.quoteId ?? it.title}`,
      }))
      .filter((o) => o.value);
  }, [itinerariesQuery.data]);

  // Templates whose URL button needs a published itinerary link are useless
  // (and confusing) on a lead with nothing published - hide them until the
  // versions have actually loaded and at least one exists.
  // ...and a call-permission request has nothing left to ask a customer who
  // already allowed calls for good (Meta refuses it).
  const pickableTemplates = templates.filter(
    (x) =>
      ((x.buttonParams?.length ?? 0) === 0 || versionOptions.length > 0) &&
      !(x.callPermission && callAllowedForGood),
  );

  // Default the button suffix to the latest published version as soon as
  // options land (and whenever the template changes).
  useEffect(() => {
    const latest = versionOptions[0]?.value;
    if (!needsVersionPicker || !latest || versionTouched) return;
    setButtonParams((prev) => {
      if (prev[0] === latest) return prev;
      const next = [...prev];
      next[0] = latest;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsVersionPicker, versionOptions, templateName, versionTouched]);

  // Travel-documents templates ({{2}} what is needed, {{3}} the upload link)
  // fill from the lead's open document request - the same values the
  // automatic send used, so a hand-sent reminder never carries a typo'd link.
  const anyTemplateNeedsDocs = templates.some((x) => x.params.some(isUploadLinkParam));
  const docRequestsQuery = useLeadDocumentRequests(
    chatLeadId ?? '',
    Boolean(chatLeadId && anyTemplateNeedsDocs),
  );
  const openDocRequest = useMemo(() => {
    const rows = docRequestsQuery.data ?? [];
    // Newest first from the API: prefer one the customer can still upload to,
    // else the latest that was not cancelled (link still opens read-only).
    return (
      rows.find((r) => r.status === 'PENDING' || r.status === 'PARTIAL') ??
      rows.find((r) => r.status !== 'CANCELLED') ??
      null
    );
  }, [docRequestsQuery.data]);
  const docParamValue = useCallback(
    (label: string): string | null => {
      if (!openDocRequest) return null;
      if (isUploadLinkParam(label)) return openDocRequest.uploadUrl;
      if (/document/i.test(label)) return openDocRequest.requiredSummary || null;
      return null;
    },
    [openDocRequest],
  );
  // The request usually lands after the template is picked - fill the slots
  // that are still empty (never over a value the agent typed).
  useEffect(() => {
    const t = templates.find((x) => x.name === templateName);
    if (!t || !openDocRequest) return;
    setTemplateParams((prev) => {
      let changed = false;
      const next = t.params.map((label, i) => {
        const cur = prev[i] ?? '';
        if (cur) return cur;
        const v = docParamValue(label);
        if (v) changed = true;
        return v ?? cur;
      });
      return changed ? next : prev;
    });
  }, [templates, templateName, openDocRequest, docParamValue]);

  const pickTemplate = (name: string): void => {
    setTemplateName(name);
    setVersionTouched(false);
    const t = templates.find((x) => x.name === name);
    if (!t) return setTemplateParams([]);
    const customerFirst = (contactName ?? '').trim().split(/\s+/)[0] ?? '';
    // Best-effort prefill by what the param label LOOKS like - the agent can
    // always overtype. Works for catalog labels and for labels derived from
    // DoubleTick's own variable names ("customer_name" -> "Customer name").
    setTemplateParams(
      t.params.map((label) => {
        const l = label.toLowerCase();
        const fromDocs = docParamValue(label);
        if (fromDocs) return fromDocs;
        if (l.includes('customer') || l === 'name' || l.includes('first name'))
          return customerFirst;
        if (l.includes('agent') || l.includes('advisor')) return me?.fullName ?? '';
        if (l.includes('callerdesk') || l.includes('phone'))
          return myProfile.data?.user.callerDeskNumber ?? '';
        if (l.includes('destination') || l.includes('trip')) return destination ?? '';
        // Numbered slots with no meaningful name: WhatsApp templates almost
        // always open with the customer's name; a second slot is usually the
        // trip. Wrong guesses are visible in the inputs before sending.
        if (/^value \{\{1\}\}$/i.test(label)) return customerFirst;
        if (/^value \{\{2\}\}$/i.test(label)) return destination ?? '';
        return '';
      }),
    );
    setButtonParams((t.buttonParams ?? []).map(() => ''));
  };

  const dispatch = async (payload: SendWhatsAppPayload): Promise<void> => {
    if (isLead) await sendLead.mutateAsync({ id: target.id, payload, target: phoneTarget });
    else await sendConvo.mutateAsync({ conversationId: target.id, payload });
  };

  const sendText = async (): Promise<void> => {
    const text = draft.trim();
    if (!text || sending) return;
    await dispatch({ text });
    setDraft('');
  };

  /** File picked -> presign -> PUT to S3 -> send. The draft becomes the caption. */
  const sendAttachment = async (file: File): Promise<void> => {
    if (uploading || sending) return;
    setComposerError(null);
    setUploading(true);
    try {
      const presign = await uploadWhatsAppAttachment(file);
      await dispatch({
        attachment: {
          s3Key: presign.s3Key,
          kind: presign.kind,
          filename: file.name,
          ...(draft.trim() ? { caption: draft.trim() } : {}),
        },
      });
      setDraft('');
    } catch (err) {
      // The API client rejects with the server's ApiError envelope (a plain
      // object, not an Error), so an `instanceof Error` check hid every real
      // reason - "upload did not complete", "media URL not accessible",
      // "unsupported type" - behind the generic line.
      setComposerError(extractServerError(err).message ?? 'Attachment failed to send. Try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const stopRecordingCleanup = (): void => {
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    recordTimerRef.current = null;
    recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    recorderRef.current = null;
    setRecording(false);
    setRecordSecs(0);
  };

  const startRecording = async (): Promise<void> => {
    if (recording || uploading || sending) return;
    setComposerError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = ['audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm;codecs=opus'].find((t) =>
        MediaRecorder.isTypeSupported(t),
      );
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recordChunksRef.current = [];
      recordIntentRef.current = 'cancel';
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const send = recordIntentRef.current === 'send';
        const type = recorder.mimeType.split(';')[0] || 'audio/mp4';
        stopRecordingCleanup();
        if (!send) return;
        const raw = new Blob(recordChunksRef.current, { type });
        if (raw.size === 0) {
          setComposerError('Nothing was recorded - try again.');
          return;
        }
        // Re-encode to MP3 before sending - Meta rejects Chrome's fragmented
        // MP4 recordings ("Media upload error"); see voice-note.ts.
        setUploading(true);
        voiceClipToMp3(raw)
          .then((file) => sendAttachment(file))
          .catch(() => setComposerError('Could not process the recording - try again.'))
          .finally(() => setUploading(false));
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setRecordSecs(0);
      recordTimerRef.current = setInterval(() => setRecordSecs((v) => v + 1), 1000);
    } catch {
      setComposerError('Microphone unavailable - allow mic access in the browser and retry.');
    }
  };

  const finishRecording = (intent: 'send' | 'cancel'): void => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    recordIntentRef.current = intent;
    if (recorder.state !== 'inactive') recorder.stop();
    else stopRecordingCleanup();
  };

  // Leaving the chat mid-recording must release the microphone.
  useEffect(() => () => stopRecordingCleanup(), []);

  const sendLocation = async (location: PickedLocation): Promise<void> => {
    if (sending) return;
    setComposerError(null);
    try {
      await dispatch({ location });
      setShowLocationPanel(false);
    } catch (err) {
      setComposerError(err instanceof Error ? err.message : 'Location failed to send.');
    }
  };

  const sendTemplate = async (): Promise<void> => {
    if (!template || sending) return;
    await dispatch({
      templateName: template.name,
      templateParams,
      ...(buttonParams.length > 0 ? { buttonParams } : {}),
    });
    setTemplateName('');
    setTemplateParams([]);
    setButtonParams([]);
    setShowTemplatePanel(false);
  };

  return (
    <>
      {/* Header */}
      <div className={hdr.bar}>
        {onBack && (
          <button type="button" onClick={onBack} aria-label="Back" className={hdr.btn}>
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <span className={hdr.avatar}>{initialsOf(contactName, phone ?? '')}</span>
        <span className="min-w-0 flex-1">
          <span
            className={`flex items-center gap-1.5 truncate font-semibold ${isPage ? 'text-[15px]' : 'text-[14px]'}`}
          >
            <span className="truncate">{contactName ?? (phone ? `+${phone}` : '…')}</span>
            {notOnWhatsApp && <NotOnWhatsAppBadge />}
          </span>
          <span className={hdr.sub}>
            {phone ? `+${phone}` : ''}
            {otherPhone ? (phoneTarget === 'ALTERNATE' ? ' · alternate' : ' · primary') : ''}
            {ownerLead
              ? ` · ${ownerLead.reference} · ${ownerLead.assignedAgentName ?? 'Unassigned'}`
              : ''}
          </span>
        </span>
        {otherPhone && (
          <button
            type="button"
            title={`Chat on the ${
              phoneTarget === 'PRIMARY' ? 'alternate' : 'primary'
            } number (+${otherPhone})`}
            aria-label="Switch number"
            onClick={() => onSwitchNumber(phoneTarget === 'PRIMARY' ? 'ALTERNATE' : 'PRIMARY')}
            className={hdr.btn}
          >
            <ArrowLeftRight className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          title={
            inCall
              ? 'Call in progress'
              : linesFull
                ? `All ${lines?.total ?? 0} calling lines are busy - the button returns when one frees up`
                : callPermissionHint(callPermission, contactName)
          }
          aria-label="Call on WhatsApp"
          disabled={!phone || inCall || linesFull}
          onClick={() => phone && void softphone.dial(phone, contactName ?? null)}
          className={hdr.btn}
        >
          <Phone className="h-4 w-4" />
        </button>
        <button
          type="button"
          title="Call log"
          aria-label="Call log"
          aria-pressed={showCallPanel}
          onClick={() => setShowCallPanel((open) => !open)}
          className={`${hdr.btn} ${showCallPanel ? hdr.btnOn : ''}`}
        >
          <History className="h-4 w-4" />
        </button>
        {leadForNav && (
          <button
            type="button"
            title="Open lead"
            onClick={() => navigate(`/leads/${leadForNav.id}`)}
            className={hdr.btn}
          >
            <ExternalLink className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Call log - voice calls are a separate stream from messages, so they
          get their own panel rather than being interleaved into the thread. */}
      {showCallPanel && (
        <div className="max-h-56 overflow-y-auto border-b border-zinc-200 bg-white">
          <WhatsAppCallHistory
            {...(isLead
              ? { leadId: target.id }
              : { conversationId: convoThread.data?.conversation.id })}
          />
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex flex-1 flex-col gap-1.5 overflow-y-auto bg-[#ECE5DD] p-3"
      >
        {loading ? (
          <p className="m-auto text-sm text-zinc-500">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="m-auto px-6 text-center text-sm text-zinc-500">
            No messages yet - send a template below to start the conversation. Free text unlocks
            once the customer replies.
          </p>
        ) : (
          messages.map((m) =>
            m.kind === 'CALL_PERMISSION' ? (
              // A call-permission answer: a note in the thread, not a bubble.
              <div
                key={m.id}
                className="max-w-[90%] self-center rounded-md bg-white/90 px-3 py-1 text-center text-[12px] text-zinc-600 shadow-sm"
              >
                {m.body}
              </div>
            ) : (
              <div
                key={m.id}
                className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-[13.5px] shadow-sm ${
                  m.direction === 'OUT'
                    ? 'self-end rounded-tr-none bg-[#DCF8C6] text-zinc-900'
                    : 'self-start rounded-tl-none bg-white text-zinc-900'
                }`}
              >
                {/* No "Template · name" strap line: past the 24h window a typed
                  message is delivered as a template, and the agent should see
                  the message they sent, not the plumbing. The template name
                  stays on the row for history and support. */}
                {/* A template's header media is always an IMAGE on the send
                  path, so render it as one instead of the generic file card. */}
                {m.mediaUrl && (
                  <MediaBubble
                    mediaUrl={m.mediaUrl}
                    kind={m.kind === 'TEMPLATE' ? templateMediaKind(m.templateView) : m.kind}
                    body={m.body}
                  />
                )}
                {m.kind === 'TEXT' && m.body && <ShareLinkPreview text={m.body} />}
                {m.templateView?.headerText && (
                  <div className="mb-0.5 font-semibold break-words">
                    {m.templateView.headerText}
                  </div>
                )}
                {/* Media bubbles show the body as the caption UNDER the media
                  (WhatsApp order); text-only bubbles keep the kind fallback. */}
                {/* LOCATION/DOCUMENT cards carry their own text, and an AUDIO
                  body is just the filename - WhatsApp shows nothing there. */}
                {(m.body || !m.mediaUrl) &&
                  !(
                    m.mediaUrl &&
                    (m.kind === 'LOCATION' || m.kind === 'DOCUMENT' || m.kind === 'AUDIO')
                  ) && (
                    <div className="break-words whitespace-pre-wrap">
                      {m.body ? (
                        <LinkifiedText text={m.body} />
                      ) : m.kind === 'TEMPLATE' ? (
                        'Template message'
                      ) : (
                        `[${m.kind.toLowerCase()}]`
                      )}
                    </div>
                  )}
                {m.templateView?.footer && (
                  <div className="mt-1 text-[12px] break-words text-zinc-500">
                    {m.templateView.footer}
                  </div>
                )}
                <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-zinc-500">
                  {timeFull(m.sentAt ?? m.createdAt)}
                  {m.direction === 'OUT' && <StatusTick status={m.status} />}
                </div>
                {m.templateView && m.templateView.buttons.length > 0 && (
                  <TemplateButtons buttons={m.templateView.buttons} />
                )}
                {/* No failure text and no red tick: the agent cannot fix a
                  closed window or an unapproved template, and the admin
                  WhatsApp log records every one with its reason. */}
              </div>
            ),
          )
        )}
      </div>

      {/* No composer for a number Meta bounced as not on WhatsApp: the API
          refuses the send anyway, and the automatic notifications skip it. */}
      {notOnWhatsApp ? (
        <div className="border-t border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-800">
          <span className="font-semibold">Not on WhatsApp.</span> The last message to this number
          could not be delivered because it has no WhatsApp account. Call or email the customer
          instead. The chat reopens on its own if they ever message us from this number.
        </div>
      ) : (
        <>
          {/* Composer. The 24h window is deliberately invisible here: typed text
          always sends (the API wraps it in the free-text template once the
          window has closed), and the rare media-past-window refusal surfaces
          as an ordinary composer error. */}
          <div className="border-t border-zinc-200 bg-zinc-50 p-2">
            {/* Composer errors are swallowed too - see the admin WhatsApp log. */}
            <div className="relative flex items-center gap-1.5">
              {/* "+" opens the WhatsApp-style attach menu (file / location /
                template) instead of a row of separate buttons. */}
              <button
                type="button"
                onClick={() => {
                  setShowAttachMenu((v) => !v);
                  setShowEmoji(false);
                }}
                title="Attach"
                aria-label="Attach"
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-zinc-600 transition-colors ${
                  showAttachMenu || showTemplatePanel || showLocationPanel
                    ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                    : 'border-zinc-200 bg-white hover:bg-zinc-100'
                }`}
              >
                <Plus
                  className={`h-5 w-5 transition-transform ${showAttachMenu ? 'rotate-45' : ''}`}
                />
              </button>
              {showAttachMenu && (
                <div className="absolute bottom-12 left-0 z-10 w-52 overflow-hidden rounded-xl border border-zinc-200 bg-white py-1 shadow-xl">
                  <button
                    type="button"
                    disabled={uploading || sending || !canSendMedia}
                    title={canSendMedia ? undefined : mediaBlockedHint}
                    onClick={() => {
                      setShowAttachMenu(false);
                      fileInputRef.current?.click();
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[13px] text-zinc-800 hover:bg-zinc-50 disabled:opacity-50"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-violet-700">
                      <Paperclip className="h-4 w-4" />
                    </span>
                    Photos & documents
                  </button>
                  <button
                    type="button"
                    disabled={!canSendMedia}
                    title={canSendMedia ? undefined : mediaBlockedHint}
                    onClick={() => {
                      setShowAttachMenu(false);
                      setShowEmoji(false);
                      setShowLocationPanel((v) => !v);
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[13px] text-zinc-800 hover:bg-zinc-50 disabled:opacity-50"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                      <MapPin className="h-4 w-4" />
                    </span>
                    Location
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAttachMenu(false);
                      setShowEmoji(false);
                      setShowLocationPanel(false);
                      setShowTemplatePanel((v) => !v);
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[13px] text-zinc-800 hover:bg-zinc-50"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                      <FileText className="h-4 w-4" />
                    </span>
                    Template
                  </button>
                </div>
              )}
              {/* Attachment: presign -> browser PUT -> send. Typed text rides as caption. */}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp,video/mp4,video/3gpp,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void sendAttachment(file);
                }}
              />
              <button
                type="button"
                onClick={() => {
                  setShowEmoji((v) => !v);
                  setShowAttachMenu(false);
                  setShowLocationPanel(false);
                }}
                title="Emoji"
                aria-label="Emoji"
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors ${
                  showEmoji
                    ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                    : 'border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-100'
                }`}
              >
                <Smile className="h-4.5 w-4.5" />
              </button>
              {recording ? (
                <div className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-full border border-rose-200 bg-white px-3">
                  <button
                    type="button"
                    onClick={() => finishRecording('cancel')}
                    title="Discard recording"
                    aria-label="Discard recording"
                    className="text-zinc-500 hover:text-rose-600"
                  >
                    <Trash2 className="h-4.5 w-4.5" />
                  </button>
                  <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-rose-500" />
                  <span className="flex-1 font-mono text-sm text-zinc-700">
                    {`${Math.floor(recordSecs / 60)}:${(recordSecs % 60).toString().padStart(2, '0')}`}
                  </span>
                  <span className="text-[11px] text-zinc-400">Recording…</span>
                </div>
              ) : (
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void sendText();
                    }
                  }}
                  placeholder={uploading ? 'Uploading attachment…' : 'Type a message'}
                  className="h-10 min-w-0 flex-1 rounded-full border border-zinc-200 bg-white px-4 text-sm outline-none focus:border-emerald-400"
                />
              )}
              {recording ? (
                <button
                  type="button"
                  onClick={() => finishRecording('send')}
                  aria-label="Send voice note"
                  title="Send voice note"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white shadow"
                >
                  <Send className="h-4.5 w-4.5" />
                </button>
              ) : draft.trim() ? (
                <button
                  type="button"
                  onClick={() => void sendText()}
                  disabled={sending || uploading}
                  aria-label="Send"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white shadow disabled:opacity-50"
                >
                  <Send className="h-4.5 w-4.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void startRecording()}
                  disabled={sending || uploading || !canSendMedia}
                  aria-label="Record a voice note"
                  title={canSendMedia ? 'Record a voice note' : mediaBlockedHint}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white shadow disabled:opacity-50"
                >
                  <Mic className="h-4.5 w-4.5" />
                </button>
              )}
            </div>
            {showEmoji && (
              <div className="mt-2 overflow-hidden rounded-md border border-zinc-200">
                <Suspense
                  fallback={
                    <p className="bg-white p-3 text-center text-xs text-zinc-400">
                      Loading emojis…
                    </p>
                  }
                >
                  <EmojiPicker
                    width="100%"
                    height={320}
                    skinTonesDisabled
                    previewConfig={{ showPreview: false }}
                    onEmojiClick={(emoji) => setDraft((d) => d + emoji.emoji)}
                  />
                </Suspense>
              </div>
            )}
            {showLocationPanel && (
              <LocationPicker
                sending={sending}
                onSend={(loc) => void sendLocation(loc)}
                onClose={() => setShowLocationPanel(false)}
              />
            )}
          </div>
          {showTemplatePanel && (
            <div className="border-t border-amber-200 bg-amber-50 p-2.5">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                  <FileText className="h-3.5 w-3.5" /> Send a template
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setShowTemplatePanel(false);
                    setTemplateName('');
                    setTemplateParams([]);
                    setButtonParams([]);
                  }}
                  aria-label="Close template panel"
                  title="Close"
                  className="rounded p-1 text-amber-700/60 hover:bg-amber-100 hover:text-amber-900"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex flex-col gap-1.5">
                <select
                  value={templateName}
                  onChange={(e) => pickTemplate(e.target.value)}
                  className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-xs"
                >
                  <option value="">
                    {templatesQuery.isLoading
                      ? 'Loading templates…'
                      : pickableTemplates.length === 0
                        ? 'No live templates available'
                        : 'Pick a template…'}
                  </option>
                  {pickableTemplates.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.label}
                    </option>
                  ))}
                </select>
                {template && (
                  <div className="flex flex-wrap gap-1.5">
                    {template.params.map((label, i) => (
                      <input
                        key={label}
                        placeholder={label}
                        value={templateParams[i] ?? ''}
                        onChange={(e) =>
                          setTemplateParams((prev) => {
                            const next = [...prev];
                            next[i] = e.target.value;
                            return next;
                          })
                        }
                        className="h-8 min-w-0 flex-1 basis-[45%] rounded-md border border-zinc-200 bg-white px-2 text-xs"
                      />
                    ))}
                    {(template.buttonParams ?? []).map((label, i) =>
                      i === 0 && versionOptions.length > 0 ? (
                        <select
                          key={`btn-${label}`}
                          value={buttonParams[0] ?? ''}
                          onChange={(e) => {
                            setVersionTouched(true);
                            setButtonParams((prev) => {
                              const next = [...prev];
                              next[0] = e.target.value;
                              return next;
                            });
                          }}
                          title="Which published version the button links to"
                          className="h-8 min-w-0 flex-1 basis-full rounded-md border border-emerald-200 bg-white px-2 text-xs"
                        >
                          {versionOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          key={`btn-${label}`}
                          placeholder={`Button: ${label}`}
                          value={buttonParams[i] ?? ''}
                          onChange={(e) =>
                            setButtonParams((prev) => {
                              const next = [...prev];
                              next[i] = e.target.value;
                              return next;
                            })
                          }
                          className="h-8 min-w-0 flex-1 basis-[45%] rounded-md border border-emerald-200 bg-white px-2 text-xs"
                        />
                      ),
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => void sendTemplate()}
                  disabled={
                    sending ||
                    !template ||
                    templateParams.some((p) => !p.trim()) ||
                    buttonParams.some((p) => !p.trim())
                  }
                  className="flex h-8 items-center justify-center gap-1.5 rounded-md bg-[#25D366] text-xs font-semibold text-white shadow disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" /> Send template
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};
